"use client"

import { usePathname } from 'next/navigation';
import { Button } from './button';
import { getTranslatedPath } from '@/lib/i18n/path-mappings';
import { BritishFlag, GermanFlag, ChineseFlag } from '@/components/icons/flags';
import { useState } from 'react';
import { Locale, locales } from '@/lib/i18n';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown } from 'lucide-react';

const localeConfig = {
  'en': { label: 'EN', flag: BritishFlag, name: 'English' },
  'de': { label: 'DE', flag: GermanFlag, name: 'Deutsch' },
  'zh-CN': { label: 'ZH', flag: ChineseFlag, name: '简体中文' },
} as const;

export function LocaleSwitch() {
  const pathname = usePathname();
  const [isLoading, setIsLoading] = useState(false);

  // Detect current locale from pathname
  const getCurrentLocale = (): Locale => {
    if (pathname.startsWith('/de/') || pathname === '/de') return 'de';
    if (pathname.startsWith('/zh-CN/') || pathname === '/zh-CN') return 'zh-CN';
    if (pathname.startsWith('/en/') || pathname === '/en') return 'en';
    return 'en'; // default
  };

  const currentLocale = getCurrentLocale();
  const CurrentFlag = localeConfig[currentLocale].flag;

  const switchLocale = async (nextLocale: Locale) => {
    if (nextLocale === currentLocale) return;

    setIsLoading(true);
    try {
      // Set the locale cookie before redirecting
      document.cookie = `NEXT_LOCALE=${nextLocale}; path=/`;

      // Remove current locale from pathname
      let pathWithoutLocale = pathname;
      for (const locale of locales) {
        if (pathname.startsWith(`/${locale}/`)) {
          pathWithoutLocale = pathname.slice(locale.length + 1); // Remove /locale
          break;
        } else if (pathname === `/${locale}`) {
          pathWithoutLocale = '/';
          break;
        }
      }

      // If we're on the homepage
      if (pathWithoutLocale === '/' || pathWithoutLocale === '') {
        window.location.assign(`/${nextLocale}`);
        return;
      }

      // Translate path if needed
      const translatedPath = await getTranslatedPath(pathWithoutLocale, currentLocale);
      window.location.assign(`/${nextLocale}${translatedPath.startsWith('/') ? translatedPath : '/' + translatedPath}`);
    } catch (error) {
      console.error('Error switching locale:', error);
      // Fallback: Go to homepage of target locale
      document.cookie = `NEXT_LOCALE=${nextLocale}; path=/`;
      window.location.assign(`/${nextLocale}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="flex items-center gap-2 w-auto px-3 justify-center hover:bg-primary/10"
          disabled={isLoading}
        >
          {isLoading ? (
            <span className="animate-pulse">...</span>
          ) : (
            <>
              <CurrentFlag />
              <span>{localeConfig[currentLocale].label}</span>
              <ChevronDown className="h-3 w-3 opacity-50" />
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40">
        {Object.entries(localeConfig).map(([locale, config]) => {
          const Flag = config.flag;
          return (
            <DropdownMenuItem
              key={locale}
              onClick={() => switchLocale(locale as Locale)}
              className="flex items-center gap-2 cursor-pointer"
              disabled={locale === currentLocale}
            >
              <Flag />
              <span className="flex-1">{config.name}</span>
              {locale === currentLocale && (
                <span className="text-xs text-primary">✓</span>
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
